import { Attributes, Range } from "@tiptap/core";
import { Image } from "../Image";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    imageBlock: {
      setImageBlock: (attributes: { src: string }) => ReturnType;
      setImageBlockAt: (attributes: {
        src: string;
        pos: number | Range;
      }) => ReturnType;
      setImageBlockAlign: (align: "left" | "center" | "right") => ReturnType;
      setImageBlockWidth: (width: number) => ReturnType;
    };
  }
}

export const ImageBlock = Image.extend({
  name: "imageBlock",

  group: "block",

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element: HTMLElement) => element.getAttribute("src"),
        renderHTML: (attributes: Attributes) => ({
          src: attributes.src,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-width"),
        renderHTML: (attributes: Attributes) => ({
          "data-width": attributes.width,
        }),
      },
      align: {
        default: "center",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-align"),
        renderHTML: (attributes: Attributes) => ({
          "data-align": attributes.align,
        }),
      },
      alt: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.getAttribute("alt"),
        renderHTML: (attributes: Attributes) => ({
          alt: attributes.alt,
        }),
      },
      link: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-link"),
        renderHTML: (attributes: Attributes) => ({
          "data-link": attributes.link,
        }),
      },
      target: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-target"),
        renderHTML: (attributes: Attributes) => ({
          "data-target": attributes.target,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src*="tiptap.dev"]:not([src^="data:"]), img[src*="windows.net"]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const hasLink = HTMLAttributes["data-link"];
    const alignOptions = {
      center: "center",
      left: "left",
      right: "right",
    };
    const align =
      alignOptions[HTMLAttributes["data-align"] as keyof typeof alignOptions] ??
      "center";
    const width = HTMLAttributes['data-width'] ?? '100%';

    const wrapperStyles = `text-align: ${align}; width: 100%;`;
    const linkStyles = 'display: inline-block;';
    const imageStyles = `width: ${width}; border-radius: 10px; margin-top: 10px; margin-bottom: 10px;`;

    if (hasLink) {
      return [
        "div",
        {
          style: wrapperStyles,
        },
        [
          "a",
          {
            href: HTMLAttributes["data-link"],
            target: HTMLAttributes["data-target"],
            style: linkStyles,
          },
          [
            "img",
            {
              ...HTMLAttributes,
              style: imageStyles,
            },
          ],
        ],
      ];
    } else {
      return [
        "div",
        {
          style: wrapperStyles,
        },
        [
          "img",
          {
            ...HTMLAttributes,
            style: imageStyles,
          },
        ],
      ];
    }
  },
});

export default ImageBlock;
